import { Injectable } from '@angular/core';
import { ApiListResponse } from 'src/app/api/api';
import { ApiSearch } from 'src/app/api/services/api-utils.service';
import type { Folder, FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';

export type FolderTreeItem = {
  id: string;
  name: string;
  parentId?: string;
  children: FolderTreeItem[];
};

export type FolderPage = {
  items: FolderItem[];
  cursor: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class FolderService {
  folderTree: FolderTreeItem[] = [];
  folderPathTree: FolderTreeItem[] = [];
  folderMap: Map<string, FolderTreeItem> = new Map();

  constructor(private foldersApiService: FoldersApiService) {}

  async loadFolders(type: string, options: ApiSearch): Promise<FolderItem[]> {
    const { query, includeSubcompanies, sortBy, reverse } = options;
    const folderQuery = this.buildFolderQuery(type, options);
    const folders: Folder[] = [];
    const items: FolderItem[] = [];
    let cursor: null | string = null;

    do {
      const response = await this.foldersApiService.list(
        {
          query: folderQuery,
          includeSubcompanies,
          sortBy: sortBy,
          reverse: reverse,
        },
        cursor,
      );

      folders.push(...response.items);
      items.push(...response.items.map((item) => this.toFolderItem(item)));
      cursor = response.cursor || response.nextPageToken;
    } while (cursor);
    if (!query && options.folderId === undefined) {
      this.loadTree(folders);
    }
    return items;
  }

  /**
   * Loads a single page of folders. Unlike `loadFolders`, it does not drain
   * every page, so folders can take part in the list's scroll pagination.
   * It also does not populate `folderTree`, which is not knowable while
   * folders are paginated.
   */
  async loadFolderPage(
    type: string,
    options: ApiSearch,
    cursor: string | null,
  ): Promise<FolderPage> {
    const { includeSubcompanies, sortBy, reverse, count } = options;
    const response = await this.foldersApiService.list(
      {
        query: this.buildFolderQuery(type, options),
        includeSubcompanies,
        sortBy,
        reverse,
        count,
      },
      cursor,
    );

    return {
      items: response.items.map((item) => this.toFolderItem(item)),
      cursor: response.cursor || response.nextPageToken || null,
    };
  }

  private buildFolderQuery(type: string, options: ApiSearch): string {
    const { query } = options;
    const nameQuery = query ? ` AND name:~"${query}"` : '';
    // A search spans every level, so it is only scoped by parent when not searching.
    const parentQuery =
      !query && options.folderId !== undefined
        ? ` AND parentId:"${options.folderId || FoldersApiService.ROOT_FOLDER_ID}"`
        : '';

    return `kind:~"${type}"${nameQuery}${parentQuery}`;
  }

  private toFolderItem(folder: Folder): FolderItem {
    return {
      ...folder,
      isFolder: true,
    } as FolderItem;
  }

  loadTree(folders: Folder[]): void {
    this.folderTree = [];
    this.folderMap.clear();

    // First pass: create map of folder items
    folders.forEach((folder) => {
      if (folder.id) {
        const folderItem: FolderTreeItem = {
          id: folder.id,
          name: folder.name,
          parentId: folder.parentId,
          children: [],
        };
        this.folderMap.set(folder.id, folderItem);
      }
    });

    // Second pass: build tree structure
    for (const folderItem of this.folderMap.values()) {
      if (!folderItem.parentId) {
        this.folderTree.push(folderItem);
      } else {
        const parentItem = this.folderMap.get(folderItem.parentId);
        if (parentItem) {
          parentItem.children.push(folderItem);
        } else {
          this.folderTree.push(folderItem);
        }
      }
    }
  }

  async loadFolderPath(folderId: string | null | undefined): Promise<void> {
    try {
      const folders = await this.getFolderPath(folderId);
      this.folderPathTree = this.buildFolderPathTree(folders);
    } catch (error) {
      console.debug('Failed to load folder path.', error);
      this.folderPathTree = [];
    }
  }

  deleteFolder(folderId: string) {
    return this.foldersApiService.delete(folderId);
  }

  showFolderItem(
    folderId: string | null | undefined,
    itemFolderId: string | null | undefined,
  ): boolean {
    return (!folderId && !itemFolderId) || folderId === itemFolderId;
  }

  /**
   * Resolves every item inside a folder and its whole subtree. The API filters
   * by folder hierarchy, so this is a single paginated query rather than one
   * request per subfolder.
   */
  async getItemsFromFolderAndSubfolders<T extends { id?: string }>(
    folderId: string,
    service: HasFolderStructure<T>,
  ): Promise<T[]> {
    const items: T[] = [];
    let cursor: string | null = null;

    do {
      const response = await service.listByFolderHierarchy(folderId, cursor);
      items.push(...response.items);
      cursor = response.cursor || response.nextPageToken;
    } while (cursor);

    return items;
  }

  private buildFolderPathTree(folders: FolderTreeItem[]): FolderTreeItem[] {
    for (let i = 0; i < folders.length - 1; i++) {
      folders[i].children = [folders[i + 1]];
    }

    return folders.length ? [folders[0]] : [];
  }

  private async getFolderPath(
    folderId: string | null | undefined,
    visitedFolderIds = new Set<string>(),
  ): Promise<FolderTreeItem[]> {
    if (
      !folderId ||
      folderId === FoldersApiService.ROOT_FOLDER_ID ||
      visitedFolderIds.has(folderId)
    ) {
      return [];
    }

    visitedFolderIds.add(folderId);
    const folder = (await this.foldersApiService.get(folderId)).item;
    if (!folder?.id) {
      return [];
    }

    return [
      ...(await this.getFolderPath(folder.parentId, visitedFolderIds)),
      this.toFolderTreeItem(folder),
    ];
  }

  private toFolderTreeItem(folder: Folder): FolderTreeItem {
    return {
      id: folder.id,
      name: folder.name,
      parentId: folder.parentId,
      children: [],
    };
  }
}

export interface HasFolderStructure<T> {
  /**
   * Lists items in a folder and every folder below it, in one query.
   */
  listByFolderHierarchy: (
    folderId: string,
    cursor?: string | null,
  ) => Promise<ApiListResponse<T>>;
}
